import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuthStore, can } from '../auth/store';
import {
  useAdminUsers,
  useCreateUser,
  useSystemSettings,
  useUpdateSystemSetting,
  useStorageStatus,
  useRbacPermissions,
  useRbacRoles,
  useUpdateRolePermissions,
  type SystemSettingsBag,
  type AdminUser,
} from '../api/queries/settings';
import {
  useLookups,
  useCreateLookup,
  useUpdateLookup,
  useDeleteLookup,
  activeOptionsFor,
} from '../api/queries/lookups';
import { ApiError } from '../api/client';
import { apiErrorText } from '../utils/apiErrors';
import { LookupSelect } from '../components/LookupSelect';
import { ProfileTab } from './settings/ProfileTab';
import { UserDetailModal } from './settings/UserDetailModal';
import { AuditLogPage } from './AuditLogPage';
import { BankAccountsTab } from './settings/BankAccountsTab';
import { IntegrationsTab } from './settings/IntegrationsTab';
import { WebsiteInput } from '../components/WebsiteInput';
import { PhoneInput } from '../components/PhoneInput';
import { AddressFields } from '../components/AddressFields';
import { normalizeWebsite } from '../utils/website';
import { formatPhoneAsTyped, stripPhoneForStorage } from '../utils/phone';
import {
  addressValueFromColumns, addressValueToColumns, type AddressValue,
} from '../utils/address';
import type { LookupCategory } from '@stfors/shared-types';
import './SettingsPage.css';
import { Toggle } from '../components/Toggle';

type Tab = 'profile' | 'users' | 'rbac' | 'system' | 'lookups' | 'bank_accounts'
  | 'integrations' | 'audit';
const TABS: Tab[] = ['profile', 'users', 'rbac', 'system', 'lookups', 'bank_accounts',
  'integrations', 'audit'];

export function SettingsPage(): JSX.Element {
  const me = useAuthStore((s) => s.me)!;
  const canManageUsers = can('settings.manage_users');
  const canManageRbac = can('settings.manage_rbac');
  const canReadSettings = can('settings.read')
    || can('settings.write_company')
    || can('settings.write_security');
  // Lookup-list management is grouped with company-wide config; only
  // admins (and `settings.write_company` grant holders) get it.
  const canManageLookups = can('settings.write_company');
  // Company bank accounts — visible to anyone who can read them (so the
  // payment picker's data owner can see the list); edits gated on write.
  const canReadBankAccounts = can('bank_account.read');
  // Audit log was previously a top-level header link; it's now a tab
  // inside Settings, gated on the same permission as before.
  const canReadAudit = can('audit.read');
  // Calendar OAuth credentials. Write-gated only: there is nothing here worth
  // reading if you cannot change it, and the client secret is never sent
  // regardless of permission.
  const canManageIntegrations = can('settings.write_security');

  // Tab lives in the URL (?tab=users) so other pages can deep-link a specific
  // tab (e.g. the person page's "Login account" note → Settings → Users).
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: Tab = TABS.includes(tabParam as Tab) ? (tabParam as Tab) : 'profile';
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(searchParams);
    if (t === 'profile') next.delete('tab');
    else next.set('tab', t);
    setSearchParams(next, { replace: true });
  };

  return (
    <section className="page-stack page-stack-wide">
      <div className="card set-toolbar">
        <h2 className="set-toolbar-heading">Settings</h2>
        <nav className="set-tabs">
        <button
          type="button"
          className={`set-tab ${tab === 'profile' ? 'active' : ''}`}
          onClick={() => setTab('profile')}
        >
          Profile
        </button>
        {canManageUsers && (
          <button
            type="button"
            className={`set-tab ${tab === 'users' ? 'active' : ''}`}
            onClick={() => setTab('users')}
          >
            Users
          </button>
        )}
        {canManageRbac && (
          <button
            type="button"
            className={`set-tab ${tab === 'rbac' ? 'active' : ''}`}
            onClick={() => setTab('rbac')}
          >
            Roles & permissions
          </button>
        )}
        {canReadSettings && (
          <button
            type="button"
            className={`set-tab ${tab === 'system' ? 'active' : ''}`}
            onClick={() => setTab('system')}
          >
            System
          </button>
        )}
        {canManageLookups && (
          <button
            type="button"
            className={`set-tab ${tab === 'lookups' ? 'active' : ''}`}
            onClick={() => setTab('lookups')}
          >
            Lookups
          </button>
        )}
        {canReadBankAccounts && (
          <button
            type="button"
            className={`set-tab ${tab === 'bank_accounts' ? 'active' : ''}`}
            onClick={() => setTab('bank_accounts')}
          >
            Bank accounts
          </button>
        )}
        {canManageIntegrations && (
          <button
            type="button"
            className={`set-tab ${tab === 'integrations' ? 'active' : ''}`}
            onClick={() => setTab('integrations')}
          >
            Integrations
          </button>
        )}
        {canReadAudit && (
          <button
            type="button"
            className={`set-tab ${tab === 'audit' ? 'active' : ''}`}
            onClick={() => setTab('audit')}
          >
            Audit
          </button>
        )}
        </nav>
      </div>
      {tab === 'profile' && <ProfileTab email={me.user.email} />}
      {tab === 'users' && canManageUsers && <UsersTab />}
      {tab === 'rbac' && canManageRbac && <RbacTab />}
      {tab === 'system' && canReadSettings && <SystemTab />}
      {tab === 'lookups' && canManageLookups && <LookupsTab />}
      {tab === 'bank_accounts' && canReadBankAccounts && <BankAccountsTab />}
      {tab === 'integrations' && canManageIntegrations && <IntegrationsTab />}
      {tab === 'audit' && canReadAudit && <AuditLogPage />}
    </section>
  );
}

// ---------- Users tab ----------

function UsersTab(): JSX.Element {
  const [showInactive, setShowInactive] = useState(false);
  const usersQuery = useAdminUsers(showInactive);
  const rolesQuery = useRbacRoles();
  const create = useCreateUser();

  // The row-level actions (roles / active / reset pw / force logout) live in the
  // detail modal now; clicking a row opens it.
  const [selected, setSelected] = useState<AdminUser | null>(null);

  const [showNew, setShowNew] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newRoles, setNewRoles] = useState<string[]>(['examiner']);
  const [createErr, setCreateErr] = useState<string | null>(null);

  const allRoleNames = (rolesQuery.data?.roles ?? []).map((r) => r.name);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    setCreateErr(null);
    if (newPw.length < 12) { setCreateErr('Password ≥ 12 chars'); return; }
    if (newRoles.length === 0) { setCreateErr('Pick at least one role'); return; }
    try {
      await create.mutateAsync({
        email: newEmail.trim().toLowerCase(),
        display_name: newName.trim(),
        password: newPw,
        role_names: newRoles,
      });
      setShowNew(false);
      setNewEmail(''); setNewName(''); setNewPw(''); setNewRoles(['examiner']);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setCreateErr('Email already in use');
      else if (err instanceof ApiError && err.status === 400) setCreateErr('Validation failed');
      else setCreateErr('Could not create user');
    }
  };

  if (usersQuery.isPending) return <p className="set-status">Loading users…</p>;
  if (usersQuery.error) {
    return <p className="set-status set-error">Failed to load.</p>;
  }
  const users = usersQuery.data?.users ?? [];

  return (
    <div className="set-tab-body">
      <div className="card">
        <header className="set-card-header">
          <h3>Users</h3>
          <div className="set-card-controls">
            <Toggle
              checked={showInactive}
              onChange={setShowInactive}
              label="Show inactive"
            />
            {!showNew && (
              <button
                type="button"
                className="btn-cyan-primary"
                onClick={() => setShowNew(true)}
              >
                New user
              </button>
            )}
          </div>
        </header>

        {showNew && (
          <form className="set-form set-card-form" onSubmit={onCreate}>
            <div className="set-form-row">
              <label className="set-field">
                <span>Email</span>
                <input type="email" className="form-input" value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)} required />
              </label>
              <label className="set-field">
                <span>Display name</span>
                <input type="text" className="form-input" value={newName}
                  onChange={(e) => setNewName(e.target.value)} required />
              </label>
              <label className="set-field">
                <span>Initial password (≥12)</span>
                <input type="text" className="form-input" value={newPw}
                  onChange={(e) => setNewPw(e.target.value)} required minLength={12} />
              </label>
            </div>
            <div className="set-field">
              <span>Roles</span>
              <div className="set-role-toggles">
                {allRoleNames.map((rn) => (
                  <Toggle
                    key={rn}
                    checked={newRoles.includes(rn)}
                    onChange={(v) => {
                        if (v) setNewRoles([...newRoles, rn]);
                        else setNewRoles(newRoles.filter((r) => r !== rn));
                      }}
                    label={rn}
                  />
                ))}
              </div>
            </div>
            {createErr && <p className="set-error" role="alert">{createErr}</p>}
            <div className="set-form-actions">
              <button type="button" className="btn-charcoal-secondary"
                onClick={() => { setShowNew(false); setCreateErr(null); }}>Cancel</button>
              <button type="submit" className="btn-cyan-primary" disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create user'}
              </button>
            </div>
          </form>
        )}

        <table className="set-table">
          <thead>
            <tr>
              <th>Display name</th>
              <th>Email</th>
              <th>Roles</th>
              <th>2FA</th>
              <th>Status</th>
              <th>Staff</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr
                key={u.id}
                className={`set-row-link${u.is_active ? '' : ' set-row-inactive'}`}
                onClick={() => setSelected(u)}
              >
                <td>{u.display_name}</td>
                <td>{u.email}</td>
                <td>
                  {u.role_names.map((r) => <span key={r} className="set-chip">{r}</span>)}
                </td>
                <td>{u.is_2fa_enabled ? <span className="set-pill set-pill-ok">ON</span> : <span className="set-pill">OFF</span>}</td>
                <td>{u.is_active ? <span className="set-pill set-pill-ok">Active</span> : <span className="set-pill set-pill-warn">Inactive</span>}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  {u.person_id !== null && can('organization.read')
                    ? <Link to={`/people/${u.person_id}`} className="set-link">View in directory ↗</Link>
                    : <span className="set-muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <UserDetailModal
          key={selected.id}
          user={selected}
          open
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

// ---------- RBAC tab ----------

function RbacTab(): JSX.Element {
  const permsQuery = useRbacPermissions();
  const rolesQuery = useRbacRoles();
  const updateRole = useUpdateRolePermissions();
  const [editingRoleId, setEditingRoleId] = useState<number | null>(null);
  const [draftPerms, setDraftPerms] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);

  if (permsQuery.isPending || rolesQuery.isPending) return <p className="set-status">Loading…</p>;
  if (permsQuery.error || rolesQuery.error) return <p className="set-status set-error">Failed to load.</p>;

  const perms = permsQuery.data?.permissions ?? [];
  const roles = rolesQuery.data?.roles ?? [];

  const startEdit = (id: number, currentPerms: string[]) => {
    setEditingRoleId(id);
    setDraftPerms(new Set(currentPerms));
    setErr(null);
  };

  const save = () => {
    if (editingRoleId === null) return;
    setErr(null);
    updateRole.mutate(
      { id: editingRoleId, permission_names: Array.from(draftPerms).sort() },
      {
        onSuccess: () => setEditingRoleId(null),
        onError: () => setErr('Save failed'),
      },
    );
  };

  // Group perms by namespace (text before the dot) for a tidy edit grid.
  const groups: Map<string, typeof perms> = new Map();
  for (const p of perms) {
    const ns = p.name.split('.')[0]!;
    if (!groups.has(ns)) groups.set(ns, []);
    groups.get(ns)!.push(p);
  }

  return (
    <div className="set-tab-body">
      <div className="card">
        <h3>Roles</h3>
        <p className="set-card-meta">
          Editing a role's permission set updates every user with that role.
          Adding new permissions or roles requires a database migration.
        </p>
        <ul className="set-role-list">
          {roles.map((r) => (
            <li key={r.id} className="set-role">
              <div className="set-role-header">
                <span className="set-role-name">{r.name}</span>
                {r.description && <span className="set-role-desc">{r.description}</span>}
                <span className="set-role-count">{r.permission_names.length} perms</span>
                {editingRoleId !== r.id && (
                  <button
                    type="button"
                    className="set-link"
                    onClick={() => startEdit(r.id, r.permission_names)}
                  >
                    edit
                  </button>
                )}
              </div>
              {editingRoleId === r.id ? (
                <div className="set-role-edit">
                  {Array.from(groups.entries()).map(([ns, nsPerms]) => (
                    <fieldset key={ns} className="set-perm-group">
                      <legend>{ns}</legend>
                      {nsPerms.map((p) => (
                        <Toggle
                          key={p.id}
                          checked={draftPerms.has(p.name)}
                          onChange={(v) => {
                              const next = new Set(draftPerms);
                              if (v) next.add(p.name);
                              else next.delete(p.name);
                              setDraftPerms(next);
                            }}
                          label={p.name.split('.')[1]}
                          title={p.description}
                          className="set-perm-toggle"
                        />
                      ))}
                    </fieldset>
                  ))}
                  {err && <p className="set-error" role="alert">{err}</p>}
                  <div className="set-form-actions">
                    <button
                      type="button"
                      className="btn-charcoal-secondary"
                      onClick={() => setEditingRoleId(null)}
                      disabled={updateRole.isPending}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn-cyan-primary"
                      onClick={save}
                      disabled={updateRole.isPending}
                    >
                      {updateRole.isPending ? 'Saving…' : 'Save grants'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="set-role-perms-readonly">
                  {r.permission_names.map((p) => (
                    <code key={p} className="set-perm-chip">{p}</code>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ---------- System tab ----------

// ---------- Lookups tab (admin-managed dropdown lists) ----------

// Turn an admin-typed display label into a stable code for code-backed
// categories, e.g. "On hold" → "on_hold". The code is what's stored and what
// any status/CSS/workflow logic keys off; the label is what's shown.
function slugify(s: string): string {
  return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 50);
}

interface LookupCatMeta {
  category: LookupCategory;
  title: string;
  description: string;
  /** Code-backed: the option has a stable code + a human label. Admins edit the
   *  label; the code is slugified from the label on add and never changes on
   *  rename. Free-text categories (undefined) store the label as the value. */
  codeBacked?: boolean;
  /** Large lists (states, street types) — collapse behind an Expand button and
   *  only fetch when opened, so opening Settings doesn't pull them every time. */
  lazy?: boolean;
  addPlaceholder?: string;
}

// One row per dropdown in the app. Adding a category here (plus the DB CHECK,
// the lookups router z.enum, and LookupCategory) surfaces it for admins.
const LOOKUP_CATEGORIES: LookupCatMeta[] = [
  { category: 'title', title: 'Titles', description: 'Honorific prefixes shown on contact forms (e.g. Mr., Dr., Hon.).', addPlaceholder: 'e.g. Capt.' },
  { category: 'suffix', title: 'Suffixes', description: 'Honorific suffixes shown on contact forms (e.g. Jr., III, Esq.).', addPlaceholder: 'e.g. Esq.' },
  { category: 'phone_type', title: 'Phone Types', description: 'Labels for the phone-numbers list on client and contractor records (e.g. Mobile, Office, Fax).', addPlaceholder: 'e.g. Direct' },
  { category: 'email_type', title: 'Email Types', description: 'Labels for the emails list on directory people (e.g. Work, Personal).', addPlaceholder: 'e.g. Billing' },
  { category: 'address_type', title: 'Address Types', description: 'Labels for the addresses list on directory people (e.g. Mailing, Home, Business).', addPlaceholder: 'e.g. Shipping' },
  { category: 'expense_type', title: 'Expense Types', description: 'Top-level categorization on the expense form (e.g. Travel, Equipment, Software).', addPlaceholder: 'e.g. Training' },
  { category: 'tax_category', title: 'Tax Categories', description: 'Schedule C–style tax categories for year-end reporting. Each expense can be tagged with one.', addPlaceholder: 'e.g. Utilities' },
  { category: 'generic_vendor', title: 'Generic Vendors', description: 'Stand-ins for the vendor field on an expense, so everyday spend (a tank of gas, a lunch) needs no vendor record. Picking one stores just the name — renaming or removing one here never changes expenses already recorded.', addPlaceholder: 'e.g. Car wash' },
  { category: 'project_status', title: 'Project Statuses', description: 'Workflow states for projects (e.g. New, In progress, Closed). Built-in states drive colors/workflow; added ones just appear in the dropdown.', codeBacked: true, addPlaceholder: 'e.g. On hold' },
  { category: 'case_type', title: 'Case Types', description: 'Type of matter on a project (e.g. Civil litigation, eDiscovery).', codeBacked: true, addPlaceholder: 'e.g. Arbitration' },
  { category: 'billing_type', title: 'Billing Types', description: 'How a project is billed (e.g. Hourly, Flat fee, Retainer).', codeBacked: true, addPlaceholder: 'e.g. Contingency' },
  { category: 'client_status', title: 'Client Statuses', description: 'Lifecycle stage for clients (e.g. Active, Prospect, Inactive).', codeBacked: true, addPlaceholder: 'e.g. On hold' },
  { category: 'organization_status', title: 'Company Statuses', description: 'Lifecycle stage for companies in the directory (e.g. Active, Prospect).', codeBacked: true, addPlaceholder: 'e.g. On hold' },
  { category: 'asset_type', title: 'Asset Types', description: 'Categories for lab assets/equipment (e.g. Write blocker, Drive).', codeBacked: true, addPlaceholder: 'e.g. Faraday bag' },
  { category: 'document_type', title: 'Document Types', description: 'Categories for uploaded documents (e.g. Report, Invoice, Engagement letter).', codeBacked: true, addPlaceholder: 'e.g. Affidavit' },
  { category: 'evidence_type', title: 'Evidence Types', description: 'Kinds of evidence captured at intake (e.g. Mobile device, Hard drive).', codeBacked: true, addPlaceholder: 'e.g. Drone' },
  { category: 'evidence_status', title: 'Evidence Statuses', description: 'Processing state of an evidence item (e.g. Received, Analyzing). Mostly workflow-driven.', codeBacked: true, addPlaceholder: 'e.g. On hold' },
  { category: 'evidence_event_type', title: 'Evidence Event Types', description: 'Chain-of-custody event kinds (e.g. Transferred, Imaged, Released).', codeBacked: true, addPlaceholder: 'e.g. Returned' },
  { category: 'expiring_item_category', title: 'Expiring-Item Categories', description: 'Categories for tracked expiring items (e.g. Professional license, Insurance).', codeBacked: true, addPlaceholder: 'e.g. Subscription' },
  { category: 'payment_method', title: 'Payment Methods', description: 'How contractor payments are made (e.g. Check, ACH, Wire).', codeBacked: true, addPlaceholder: 'e.g. Zelle' },
  { category: 'payment_status', title: 'Payment Statuses', description: 'State of a contractor payment (e.g. Pending, Paid, Void).', codeBacked: true, addPlaceholder: 'e.g. Scheduled' },
  { category: 'contact_role', title: 'Directory Roles', description: 'Code-backed: client, vendor, contractor (role tables) and contact (individual company affiliations via organization_contacts). Renaming changes the label only; other custom values stay inert beyond filters/dropdowns.', codeBacked: true, addPlaceholder: 'e.g. Partner' },
  { category: 'street_type', title: 'Street Types', description: 'USPS street-type suffixes for addresses (e.g. St, Ave, Blvd).', lazy: true, addPlaceholder: 'e.g. Grn' },
  { category: 'street_direction', title: 'Street Directions', description: 'Directional prefixes/suffixes for street addresses — the pre-direction before the street name (E Jefferson St) and the post-direction after the type (Pennsylvania Ave NW).', addPlaceholder: 'e.g. NNE' },
  { category: 'us_state', title: 'US States & Territories', description: 'Choices for the address state dropdown. Stored as the 2-letter code; the label is the full name.', codeBacked: true, lazy: true, addPlaceholder: 'e.g. Guam' },
];

function LookupsTab(): JSX.Element {
  return (
    <div className="set-tab-body">
      <p className="set-description">
        Manage every dropdown list used across the app. Removing a value won't
        change existing records — they keep whatever was selected at the time —
        but it will no longer appear as a choice. For statuses and types, the
        built-in options power app behavior (colors, workflow); options you add
        simply appear in the dropdown.
      </p>
      {LOOKUP_CATEGORIES.map((meta) => (
        <LookupCard key={meta.category} {...meta} />
      ))}
    </div>
  );
}

function LookupCard({
  category,
  title,
  description,
  codeBacked = false,
  lazy = false,
  addPlaceholder,
}: LookupCatMeta): JSX.Element {
  const create = useCreateLookup();
  const update = useUpdateLookup();
  const del = useDeleteLookup();
  const [expanded, setExpanded] = useState(!lazy);
  const [newValue, setNewValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Large categories defer their fetch until the card is expanded.
  const lookups = useLookups(category, { enabled: expanded });
  const options = activeOptionsFor(lookups.data?.lookup_options, category);

  // What a pill shows / a rename edits: the label for code-backed categories,
  // the value itself for free-text ones.
  const display = (o: (typeof options)[number]) => (codeBacked ? (o.label ?? o.value) : o.value);

  const onAdd = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const label = newValue.trim();
    if (label.length === 0) return;
    // Code-backed: slugify the label into a stable code; free-text: value == label.
    const value = codeBacked ? slugify(label) : label;
    if (value.length === 0) {
      setError('Enter letters or numbers.');
      return;
    }
    try {
      await create.mutateAsync(codeBacked ? { category, value, label } : { category, value });
      setNewValue('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(`"${label}" already exists in ${title.toLowerCase()}.`);
        return;
      }
      setError('Could not add. Try again.');
    }
  };

  const onDelete = async (id: number, shown: string) => {
    setError(null);
    if (!confirm(`Remove "${shown}" from ${title.toLowerCase()}?`)) return;
    try {
      await del.mutateAsync(id);
    } catch {
      setError(`Could not remove "${shown}".`);
    }
  };

  const onRename = async (id: number, shown: string) => {
    setError(null);
    const next = prompt(`Rename "${shown}" to:`, shown);
    if (next === null) return;
    const trimmed = next.trim();
    if (trimmed.length === 0 || trimmed === shown) return;
    try {
      // Code-backed rename edits only the label (the code stays fixed so
      // status/CSS/workflow keep working); free-text rename edits the value.
      await update.mutateAsync(codeBacked ? { id, label: trimmed } : { id, value: trimmed });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(`"${trimmed}" already exists.`);
        return;
      }
      setError('Rename failed.');
    }
  };

  return (
    <div className="card">
      <div className="set-lookup-head">
        <h3>{title}</h3>
        {lazy && (
          <button type="button" className="btn-charcoal-secondary" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Collapse' : 'Expand'}
          </button>
        )}
      </div>
      <p className="set-card-description">{description}</p>

      {!expanded ? (
        <p className="set-lookup-empty">Collapsed — expand to load and manage these options.</p>
      ) : (
        <>
          {lookups.isPending && <p className="set-status">Loading…</p>}
          {lookups.error && <p className="set-status set-error">Could not load this list.</p>}
          <div className="set-lookup-pills">
            {!lookups.isPending && options.length === 0 ? (
              <span className="set-lookup-empty">No options yet — add one below.</span>
            ) : (
              options.map((o) => {
                const shown = display(o);
                return (
                  <span key={o.id} className="set-lookup-pill">
                    <button
                      type="button"
                      className="set-lookup-pill-text"
                      onClick={() => onRename(o.id, shown)}
                      title={codeBacked ? `Click to rename (code: ${o.value})` : 'Click to rename'}
                      disabled={update.isPending}
                    >
                      {shown}
                    </button>
                    <button
                      type="button"
                      className="set-lookup-pill-x"
                      onClick={() => onDelete(o.id, shown)}
                      disabled={del.isPending}
                      aria-label={`Remove ${shown}`}
                      title={`Remove ${shown}`}
                    >
                      ×
                    </button>
                  </span>
                );
              })
            )}
          </div>
          <form className="set-lookup-add" onSubmit={onAdd}>
            <input
              type="text"
              className="form-input set-lookup-add-input"
              value={newValue}
              onChange={(e) => setNewValue(e.target.value)}
              placeholder={addPlaceholder ?? 'New value'}
              maxLength={50}
              disabled={create.isPending}
            />
            <button
              type="submit"
              className="btn-cyan-primary"
              disabled={create.isPending || newValue.trim().length === 0}
            >
              {create.isPending ? 'Adding…' : 'Add'}
            </button>
          </form>
          {codeBacked && newValue.trim().length > 0 && (
            <p className="set-card-description">Stored as code: <code>{slugify(newValue) || '—'}</code></p>
          )}
          {error && <p className="set-error" role="alert">{error}</p>}
        </>
      )}
    </div>
  );
}

function SystemTab(): JSX.Element {
  const settingsQ = useSystemSettings();
  const update = useUpdateSystemSetting();
  const canCompany = can('settings.write_company');
  const canSecurity = can('settings.write_security');
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const save = async (key: keyof SystemSettingsBag, value: unknown) => {
    setError(null);
    setOk(null);
    try {
      await update.mutateAsync({ key, value });
      setOk(`Saved ${key}.`);
    } catch (err) {
      // A settings save is generic — one save(key, value) behind every card — so
      // there is no inline slot to highlight; the API's own words go on the
      // card's error line. "Validation failed." is what made a real 400 here
      // undiagnosable without DevTools.
      setError(apiErrorText(err, 'Save failed.'));
    }
  };

  if (settingsQ.isPending) return <p className="set-status">Loading settings…</p>;
  if (settingsQ.error) return <p className="set-status set-error">Could not load settings.</p>;
  const s = settingsQ.data?.settings ?? {};

  return (
    <div className="set-tab-body">
      {error && <p className="set-error" role="alert">{error}</p>}
      {ok && <p className="set-success">{ok}</p>}

      <CompanyCard initial={s.company} disabled={!canCompany} onSave={(v) => save('company', v)} />
      <TimeZoneCard initial={s.timezone} disabled={!canCompany} onSave={(v) => save('timezone', v)} />
      <GenericVendorTypesCard
        initial={s.generic_vendor_types}
        disabled={!canCompany}
        onSave={(v) => save('generic_vendor_types', v)}
      />
      <PasswordPolicyCard initial={s.password_policy} disabled={!canSecurity} onSave={(v) => save('password_policy', v)} />
      <SessionPolicyCard initial={s.session_policy} disabled={!canSecurity} onSave={(v) => save('session_policy', v)} />
      <RateLimitCard initial={s.rate_limit} disabled={!canSecurity} onSave={(v) => save('rate_limit', v)} />
      <IpWhitelistCard initial={s.ip_whitelist} disabled={!canSecurity} onSave={(v) => save('ip_whitelist', v)} />
      <AuditRetentionCard initial={s.audit_retention} disabled={!canSecurity} onSave={(v) => save('audit_retention', v)} />
      <DocumentStorageCard initial={s.document_storage} disabled={!canSecurity} onSave={(v) => save('document_storage', v)} />
    </div>
  );
}

function DocumentStorageCard({ initial, disabled, onSave }: {
  initial: SystemSettingsBag['document_storage'];
  disabled: boolean;
  onSave: (v: { root_path: string }) => void;
}): JSX.Element {
  const status = useStorageStatus();
  const [rootPath, setRootPath] = useState(initial?.root_path ?? '');

  return (
    <div className="card">
      <h3>Document Storage</h3>
      <p className="set-card-meta">
        Folder the document vault writes case files to. Files are stored encrypted; leave blank to
        use the server default. Changing this only affects new uploads — files already stored under
        a previous folder stay readable automatically.
      </p>

      {status.isPending ? (
        <p className="set-status">Checking current location…</p>
      ) : status.error ? (
        <p className="set-status set-error">Could not read storage status.</p>
      ) : status.data ? (
        <div className="set-form-row">
          <label className="set-field">
            <span>Currently storing in</span>
            <input className="form-input" value={status.data.effective_root} readOnly disabled />
          </label>
          <label className="set-field">
            <span>Status</span>
            <input
              className="form-input"
              value={status.data.writable ? 'Writable ✓' : 'Not writable ✗'}
              readOnly
              disabled
            />
          </label>
        </div>
      ) : null}

      <label className="set-field">
        <span>Storage folder (absolute path; blank = server default)</span>
        <input
          className="form-input"
          value={rootPath}
          placeholder={status.data?.allowed_roots?.[0] ?? '/vault'}
          onChange={(e) => setRootPath(e.target.value)}
          disabled={disabled}
        />
      </label>
      {!disabled && status.data?.allowed_roots && status.data.allowed_roots.length > 0 && (
        <p className="set-card-meta">
          Must be within: <code>{status.data.allowed_roots.join('  ·  ')}</code>
        </p>
      )}
      {!disabled && (
        <div className="set-form-actions">
          <button type="button" className="btn-cyan-primary" onClick={() => onSave({ root_path: rootPath.trim() })}>
            Save storage folder
          </button>
        </div>
      )}
    </div>
  );
}

// Common IANA zones. The server accepts any valid zone; this is just a
// friendly shortlist for the firm's home/display time zone (used by
// invoice PDFs, etc.).
const TIME_ZONES: { zone: string; label: string }[] = [
  { zone: 'America/New_York', label: 'Eastern (New York)' },
  { zone: 'America/Chicago', label: 'Central (Chicago)' },
  { zone: 'America/Denver', label: 'Mountain (Denver)' },
  { zone: 'America/Phoenix', label: 'Mountain — no DST (Phoenix)' },
  { zone: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { zone: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { zone: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
  { zone: 'UTC', label: 'UTC' },
];

function TimeZoneCard({ initial, disabled, onSave }: {
  initial: SystemSettingsBag['timezone'];
  disabled: boolean;
  onSave: (v: NonNullable<SystemSettingsBag['timezone']>) => void;
}): JSX.Element {
  const [zone, setZone] = useState(initial?.zone ?? 'America/New_York');
  // Include the current value even if it isn't in the shortlist so it
  // still shows selected.
  const options = TIME_ZONES.some((t) => t.zone === zone)
    ? TIME_ZONES
    : [{ zone, label: zone }, ...TIME_ZONES];

  return (
    <div className="card">
      <h3>Home Time Zone</h3>
      <p className="set-card-meta">
        The firm's display time zone. Dates and the “Generated” stamp on invoice PDFs render in this
        zone (Eastern automatically shows EST or EDT depending on the date).
      </p>
      <label className="set-field">
        <span>Time zone</span>
        <select
          className="form-input"
          value={zone}
          onChange={(e) => setZone(e.target.value)}
          disabled={disabled}
        >
          {options.map((t) => (
            <option key={t.zone} value={t.zone}>{t.label}</option>
          ))}
        </select>
      </label>
      {!disabled && (
        <div className="set-form-actions">
          <button type="button" className="btn-cyan-primary" onClick={() => onSave({ zone })}>
            Save time zone
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * generic_vendor → expense_type. When a receipt's vendor is read as one of the
 * generic categories, the expense's Type is set from here.
 *
 * A setting rather than a keyword list in the code, so "we file tolls under
 * Travel, not Other" is something the firm decides. Rows come from the live
 * `generic_vendor` lookup, so adding a generic in Settings → Lookups makes it
 * mappable here without a release.
 */
function GenericVendorTypesCard({ initial, disabled, onSave }: {
  initial: SystemSettingsBag['generic_vendor_types'];
  disabled: boolean;
  onSave: (v: Record<string, string>) => void;
}): JSX.Element {
  const lookups = useLookups();
  const generics = activeOptionsFor(lookups.data?.lookup_options, 'generic_vendor');
  const [map, setMap] = useState<Record<string, string>>(initial ?? {});

  return (
    <div className="card">
      <h3>Expense Types from Receipts</h3>
      <p className="set-card-meta">
        When a receipt names one of these generic vendors, the expense's Type is set to match.
        Only applied while the type is still the default — a type picked by hand always wins.
        Leave one blank to infer nothing for it.
      </p>

      {generics.length === 0 ? (
        <p className="set-status">
          No generic vendors yet — add them under Lookups → Generic Vendors.
        </p>
      ) : (
        <div className="set-gvt-grid">
          {generics.map((g) => (
            <label key={g.id} className="set-field">
              <span>{g.value}</span>
              <LookupSelect
                category="expense_type"
                value={map[g.value] ?? ''}
                onChange={(v) => setMap((m) => {
                  const next = { ...m };
                  // Delete rather than store '' — the API validates every value
                  // against the live lookups, and '' is not one of them.
                  if (v === '') delete next[g.value];
                  else next[g.value] = v;
                  return next;
                })}
                disabled={disabled}
                allowEmpty
                emptyLabel="— no type —"
              />
            </label>
          ))}
        </div>
      )}

      {!disabled && generics.length > 0 && (
        <div className="set-form-actions">
          <button type="button" className="btn-cyan-primary" onClick={() => onSave(map)}>
            Save receipt types
          </button>
        </div>
      )}
    </div>
  );
}

/** Exported for CompanyCard.test.tsx — the rest of the page is not involved. */
export function CompanyCard({ initial, disabled, onSave }: {
  initial: SystemSettingsBag['company'];
  disabled: boolean;
  onSave: (v: NonNullable<SystemSettingsBag['company']>) => void;
}): JSX.Element {
  const [name, setName] = useState(initial?.name ?? '');
  // The firm's own address goes through the same <AddressFields> composite as
  // every other address in the app. `initial` already uses the address_line1 /
  // address_line2 naming that addressValueFromColumns accepts.
  const [addr, setAddr] = useState<AddressValue>(() => addressValueFromColumns(initial ?? null));
  const [email, setEmail] = useState(initial?.email ?? '');
  const [phone, setPhone] = useState(formatPhoneAsTyped(initial?.phone ?? ''));
  const [website, setWebsite] = useState(initial?.website ?? '');

  return (
    <div className="card">
      <h3>Company</h3>
      <div className="set-form-row">
        <label className="set-field">
          <span>Name</span>
          <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} disabled={disabled} />
        </label>
        <label className="set-field">
          <span>Email</span>
          <input className="form-input" value={email} onChange={(e) => setEmail(e.target.value)} disabled={disabled} />
        </label>
      </div>
      <div className="set-form-row">
        <label className="set-field">
          <span>Phone</span>
          <PhoneInput value={phone} onChange={setPhone} disabled={disabled} />
        </label>
        <label className="set-field">
          <span>Website</span>
          <WebsiteInput value={website} onChange={setWebsite} disabled={disabled} />
        </label>
      </div>
      {/* A <div>, not a <label> — <AddressFields> is a composite of many inputs. */}
      <div className="set-field">
        <span>Address</span>
        <AddressFields
          value={addr}
          onChange={setAddr}
          legacyText={initial?.address}
          autoParse
          disabled={disabled}
          idPrefix="company"
        />
      </div>
      {!disabled && (
        <div className="set-form-actions">
          <button type="button" className="btn-cyan-primary" onClick={() => {
            const cols = addressValueToColumns(addr);
            onSave({
              name: name.trim(),
              // Clear the legacy free-text blob once structured fields are saved
              // — same as the organization editor.
              address: null,
              address_line1: cols.line1,
              address_line2: cols.line2,
              city: cols.city,
              state: cols.state,
              postal_code: cols.postal_code,
              country: cols.country,
              email: email.trim() || null,
              phone: stripPhoneForStorage(phone) || null,
              website: normalizeWebsite(website) || null,
            });
          }}>
            Save company
          </button>
        </div>
      )}
    </div>
  );
}

function PasswordPolicyCard({ initial, disabled, onSave }: {
  initial: SystemSettingsBag['password_policy'];
  disabled: boolean;
  onSave: (v: NonNullable<SystemSettingsBag['password_policy']>) => void;
}): JSX.Element {
  const [minLen, setMinLen] = useState(initial?.min_length ?? 12);
  const [upper, setUpper] = useState(initial?.require_upper ?? false);
  const [lower, setLower] = useState(initial?.require_lower ?? false);
  const [digit, setDigit] = useState(initial?.require_digit ?? false);
  const [symbol, setSymbol] = useState(initial?.require_symbol ?? false);
  return (
    <div className="card">
      <h3>Password Policy</h3>
      <div className="set-form-row">
        <label className="set-field">
          <span>Minimum length (8–256)</span>
          <input type="number" className="form-input" value={minLen} min={8} max={256}
            onChange={(e) => setMinLen(Number(e.target.value))} disabled={disabled} />
        </label>
      </div>
      <div className="set-role-toggles">
        <Toggle
          checked={upper}
          onChange={setUpper}
          label="uppercase"
          disabled={disabled}
        />
        <Toggle
          checked={lower}
          onChange={setLower}
          label="lowercase"
          disabled={disabled}
        />
        <Toggle
          checked={digit}
          onChange={setDigit}
          label="digit"
          disabled={disabled}
        />
        <Toggle
          checked={symbol}
          onChange={setSymbol}
          label="symbol"
          disabled={disabled}
        />
      </div>
      {!disabled && (
        <div className="set-form-actions">
          <button type="button" className="btn-cyan-primary" onClick={() => onSave({
            min_length: minLen, require_upper: upper, require_lower: lower, require_digit: digit, require_symbol: symbol,
          })}>
            Save policy
          </button>
        </div>
      )}
    </div>
  );
}

function SessionPolicyCard({ initial, disabled, onSave }: {
  initial: SystemSettingsBag['session_policy'];
  disabled: boolean;
  onSave: (v: NonNullable<SystemSettingsBag['session_policy']>) => void;
}): JSX.Element {
  const [idle, setIdle] = useState(initial?.idle_timeout_minutes ?? 0);
  return (
    <div className="card">
      <h3>Session Policy</h3>
      <p className="set-card-meta">Idle timeout — 0 disables auto-logout.</p>
      <label className="set-field">
        <span>Idle timeout (minutes, 0–720)</span>
        <input type="number" className="form-input" value={idle} min={0} max={720}
          onChange={(e) => setIdle(Number(e.target.value))} disabled={disabled} />
      </label>
      {!disabled && (
        <div className="set-form-actions">
          <button type="button" className="btn-cyan-primary" onClick={() => onSave({ idle_timeout_minutes: idle })}>
            Save session policy
          </button>
        </div>
      )}
    </div>
  );
}

function RateLimitCard({ initial, disabled, onSave }: {
  initial: SystemSettingsBag['rate_limit'];
  disabled: boolean;
  onSave: (v: NonNullable<SystemSettingsBag['rate_limit']>) => void;
}): JSX.Element {
  const [login, setLogin] = useState(initial?.login_attempts_per_15min ?? 10);
  const [api, setApi] = useState(initial?.api_requests_per_minute ?? 600);
  return (
    <div className="card">
      <h3>Rate Limits</h3>
      <div className="set-form-row">
        <label className="set-field">
          <span>Login attempts / 15 min</span>
          <input type="number" className="form-input" value={login} min={0} max={100}
            onChange={(e) => setLogin(Number(e.target.value))} disabled={disabled} />
        </label>
        <label className="set-field">
          <span>API requests / minute</span>
          <input type="number" className="form-input" value={api} min={0} max={60000}
            onChange={(e) => setApi(Number(e.target.value))} disabled={disabled} />
        </label>
      </div>
      {!disabled && (
        <div className="set-form-actions">
          <button type="button" className="btn-cyan-primary" onClick={() => onSave({
            login_attempts_per_15min: login, api_requests_per_minute: api,
          })}>
            Save limits
          </button>
        </div>
      )}
    </div>
  );
}

function IpWhitelistCard({ initial, disabled, onSave }: {
  initial: SystemSettingsBag['ip_whitelist'];
  disabled: boolean;
  onSave: (v: NonNullable<SystemSettingsBag['ip_whitelist']>) => void;
}): JSX.Element {
  const [enabled, setEnabled] = useState(initial?.enabled ?? false);
  const [text, setText] = useState((initial?.cidrs ?? []).join('\n'));
  return (
    <div className="card">
      <h3>IP whitelist</h3>
      <p className="set-card-meta">When enabled, only requests from listed CIDRs reach the API. /health and /ready are always allowed.</p>
      <Toggle
        checked={enabled}
        onChange={setEnabled}
        label="Enabled"
        disabled={disabled}
      />
      <label className="set-field">
        <span>CIDRs (one per line, e.g. <code>10.0.0.0/8</code>)</span>
        <textarea className="form-input" rows={5} value={text} onChange={(e) => setText(e.target.value)} disabled={disabled} />
      </label>
      {!disabled && (
        <div className="set-form-actions">
          <button type="button" className="btn-cyan-primary" onClick={() => onSave({
            enabled,
            cidrs: text.split('\n').map((s) => s.trim()).filter((s) => s.length > 0).slice(0, 100),
          })}>
            Save whitelist
          </button>
        </div>
      )}
    </div>
  );
}

function AuditRetentionCard({ initial, disabled, onSave }: {
  initial: SystemSettingsBag['audit_retention'];
  disabled: boolean;
  onSave: (v: NonNullable<SystemSettingsBag['audit_retention']>) => void;
}): JSX.Element {
  const [days, setDays] = useState(initial?.days ?? 365);
  return (
    <div className="card">
      <h3>Audit Retention</h3>
      <p className="set-card-meta">Days to keep audit-log rows. Minimum 30, maximum 7300 (~20 years).</p>
      <label className="set-field">
        <span>Days (30–7300)</span>
        <input type="number" className="form-input" value={days} min={30} max={7300}
          onChange={(e) => setDays(Number(e.target.value))} disabled={disabled} />
      </label>
      {!disabled && (
        <div className="set-form-actions">
          <button type="button" className="btn-cyan-primary" onClick={() => onSave({ days })}>
            Save retention
          </button>
        </div>
      )}
    </div>
  );
}
